import { useCallback, useEffect, useRef, useState } from 'react';
import LiveToolbarIcon from './LiveToolbarIcon';
import LiveChoiceButtons from './LiveChoiceButtons';
import { createPortal } from 'react-dom';
import styled, { StyleSheetManager } from 'styled-components';
import api from '../../../../Component/axios_Interceptor/api';
import { SERVER_ADDRESS } from '../../../../Component/server';
import { AdminActionButton } from '../../../../Component/UI/AdminPageHeader';
import AdminCancellationDialog from '../../../../Component/UI/AdminCancellationDialog';
import SellerGiftButton from './SellerGiftButton';
import LiveDetailDialog from './LiveDetailDialog';


type Purchase = { seq: number; orderSeq?: number; remainingQuantity?: number; orderedAt: string; nickname: string; name: string; productName: string; optionName: string | null; quantity: number; amount: number; status: string };
type ProductTotal = { productSeq: number; productName: string; quantity: number };
const time = new Intl.DateTimeFormat('ko-KR', { timeZone: 'Asia/Seoul', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });

function PurchaseList({ liveSeq, full = false, ownerDocument = document, onOpen, refreshVersion = 0, orderHistoryOnly = false, history = false, canGift = false, giftDisabled = false }: {
  liveSeq: number; full?: boolean; ownerDocument?: Document; onOpen?: () => void; refreshVersion?: number; rehearsal?: boolean; orderHistoryOnly?: boolean; history?: boolean; canGift?: boolean; giftDisabled?: boolean;
}) {
  const [result, setResult] = useState<{ items: Purchase[]; total: number; totalQuantity: number; topProducts: ProductTotal[] } | null>(null);
  const [page, setPage] = useState(1), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState(''), [query, setQuery] = useState('');
  const [cancelTarget, setCancelTarget] = useState<Purchase | null>(null), [cancelReason, setCancelReason] = useState('');
  const [cancelQuantity, setCancelQuantity] = useState(1), [cancelling, setCancelling] = useState(false), [cancelError, setCancelError] = useState('');
  const cancelRequest = useRef('');
  useEffect(() => {
    if (search.trim() === query) return;
    const timer = window.setTimeout(() => { setQuery(search.trim()); setPage(1); }, 300);
    return () => window.clearTimeout(timer);
  }, [search, query]);
  const perPage = full ? pageSize : 5;
  const version = useRef(0), pending = useRef<number | null>(null);
  const load = useCallback(async () => {
    if (pending.current !== null || !Number.isSafeInteger(liveSeq) || liveSeq < 1) return;
    const request = ++version.current; pending.current = request; setBusy(true);
    try {
      const { data } = await api.get(`${SERVER_ADDRESS}orders/live-purchases`, { params: { live_seq: liveSeq, page, per_page: perPage, ...(query ? { search: query } : {}) } });
      if (request !== version.current) return;
      if (!Array.isArray(data?.items) || !Number.isInteger(data.total) || data.total < 0) throw new Error('Invalid purchases');
      const last = Math.max(1, Math.ceil(data.total / perPage));
      if (page > last) { setPage(last); return; }
      setResult(data); setError('');
    } catch { if (request === version.current) setError('구매 목록을 불러오지 못했습니다. 다시 시도해주세요.'); }
    finally { if (pending.current === request) pending.current = null; if (request === version.current) setBusy(false); }
  }, [liveSeq, page, perPage, query]);
  useEffect(() => {
    setResult(null); void load();
    const refreshVisible = () => { if (!ownerDocument.hidden) void load(); };
    const timer = window.setInterval(refreshVisible, 5000);
    ownerDocument.addEventListener('visibilitychange', refreshVisible);
    ownerDocument.defaultView?.addEventListener('focus', refreshVisible);
    return () => { version.current++; pending.current = null; window.clearInterval(timer); ownerDocument.removeEventListener('visibilitychange', refreshVisible); ownerDocument.defaultView?.removeEventListener('focus', refreshVisible); };
  }, [load, ownerDocument]);
  useEffect(() => { void load(); }, [refreshVersion, load]);
  return <Section aria-label={history ? '라이브 주문내역' : full ? '전체 실시간 구매 목록' : orderHistoryOnly ? '구매 내역' : '실시간 구매 목록'}>
    <Header><div><h2>{history ? '주문내역' : full ? '전체 실시간 구매 목록' : orderHistoryOnly ? '구매 내역' : '실시간 구매 목록'}</h2><p>{full ? '전체 구매 내역' : orderHistoryOnly ? '고객별 주문 · 결제 상태' : '구매 수량 Top 5'}{result ? ` · 합계 ${Number(result.totalQuantity).toLocaleString()}개` : ''}</p></div>
      {history ? <HistorySearch aria-label="주문자 이름·닉네임 검색" placeholder="이름·닉네임 검색" maxLength={100} value={search}
        onChange={event => setSearch(event.target.value)} onKeyDown={event => { if (event.key === 'Enter' && !event.nativeEvent.isComposing) { event.preventDefault(); setQuery(search.trim()); setPage(1); } }} />
        : full ? <AdminActionButton type="button" disabled={busy} onClick={() => { void load(); }} aria-label="구매 목록 새로고침">새로고침</AdminActionButton>
        : <AdminActionButton type="button" onClick={onOpen}>전체 보기</AdminActionButton>}
    </Header>
    {error && <p role="alert">{error} {!full && <button type="button" disabled={busy} onClick={() => { void load(); }}>다시 불러오기</button>}</p>}
    {!result && busy && <p role="status">구매 목록을 불러오는 중입니다.</p>}
    {result && !result.items.length && <EmptyState><LiveToolbarIcon name="list" /><p>{query ? '검색 결과가 없습니다.' : '아직 구매 내역이 없습니다.'}</p></EmptyState>}
    {result && !full && !orderHistoryOnly && result.topProducts?.length > 0 && <TableScroll $full={false} tabIndex={0} aria-label="상품별 구매 수량 스크롤">
      <Table $full={false}><thead><tr><th>상품</th><th>합산 수량</th></tr></thead><tbody>
        {result.topProducts.map(item => <tr key={item.productSeq}><td>{item.productName}</td><td>{Number(item.quantity).toLocaleString()}개</td></tr>)}
      </tbody></Table>
    </TableScroll>}
    {result && full && result.items.length > 0 && <TableScroll $full={full} tabIndex={0} aria-label="구매 내역 스크롤"><Table $full={full}><thead><tr><th>시간</th>{full && <th>이름</th>}{history && <th>닉네임</th>}<th>상품 · 옵션</th><th>수량</th>{full && <><th>금액</th><th>상태</th></>}</tr></thead>
      <tbody>{result.items.map(item => <tr key={item.seq}>
        <td><time dateTime={item.orderedAt} title={item.orderedAt}>{Number.isFinite(Date.parse(item.orderedAt)) ? time.format(new Date(item.orderedAt)) : '—'}</time></td>
        {full && <td>{item.name || '—'}</td>}{history && <td>{item.nickname || '—'}</td>}<td>{item.productName}{item.optionName && <small>{item.optionName}</small>}</td>
        <td>{Number(item.quantity).toLocaleString()}</td>{full && <><td>{Number(item.amount).toLocaleString()}원</td><td><StatusActions><span>{item.status}</span><div>
          {history && !!item.orderSeq && item.status !== '주문취소' && Number(item.remainingQuantity) > 0 && <AdminActionButton type="button" onClick={() => {
            setCancelTarget(item); setCancelQuantity(Number(item.remainingQuantity)); setCancelReason(''); setCancelError(''); cancelRequest.current = crypto.randomUUID();
          }}>취소</AdminActionButton>}
          {history && canGift && !!item.orderSeq && <SellerGiftButton liveSeq={liveSeq} disabled={giftDisabled}
            initialOrder={{ order_seq: item.orderSeq, nickname: item.nickname, name: item.name }} label={`${item.nickname || item.name} 연계 등록`} />}
          </div></StatusActions></td></>}
      </tr>)}</tbody></Table></TableScroll>}
    {full && <Pages aria-label="구매 목록 페이지네이션">
      <PageNavigation>
      <AdminActionButton type="button" disabled={busy || !result || page === 1} onClick={() => setPage(current => current - 1)}>이전</AdminActionButton>
      <span>{page} / {Math.max(1, Math.ceil((result?.total || 0) / perPage))}</span>
      <AdminActionButton type="button" disabled={busy || !result || page * perPage >= result.total} onClick={() => setPage(current => current + 1)}>다음</AdminActionButton>
      </PageNavigation>
      <PageSizeOptions>
      <LiveChoiceButtons label="페이지당 표시 개수" hideLabel compact value={String(pageSize)} onChange={value => { setPageSize(Number(value)); setPage(1); }}
        options={[20, 50, 100].map(value => ({ value: String(value), label: `${value}개` }))} />
      </PageSizeOptions>
    </Pages>}
    <AdminCancellationDialog open={!!cancelTarget} title="고객 품목 취소" reason={cancelReason} onReasonChange={setCancelReason}
      description={cancelTarget ? `${cancelTarget.name || cancelTarget.nickname} · ${cancelTarget.productName}${cancelTarget.optionName ? ` · ${cancelTarget.optionName}` : ''}` : undefined}
      busy={cancelling} error={cancelError} canConfirm={!!cancelTarget && Number.isSafeInteger(cancelQuantity) && cancelQuantity > 0 && cancelQuantity <= Number(cancelTarget.remainingQuantity)}
      onClose={() => setCancelTarget(null)} onConfirm={async () => {
        if (!cancelTarget || cancelling) return;
        setCancelling(true); setCancelError('');
        try {
          const { data } = await api.patch(`${SERVER_ADDRESS}orders/order-detail-update-cancel`, { request_id: cancelRequest.current,
            reason: cancelReason.trim(), orders: [{ order_seq: cancelTarget.orderSeq, items: [{ detail_seq: cancelTarget.seq, cancel_cnt: cancelQuantity }] }] });
          if (data?.success !== 1 || data?.fail) throw new Error(data?.failures?.[0]?.message || '취소 결과를 확인해주세요.');
          setCancelTarget(null); await load();
        } catch (cause: any) { setCancelError(cause?.response?.data?.message || cause?.message || '취소를 처리하지 못했습니다.'); }
        finally { setCancelling(false); }
      }}>
      <CancelQuantity>취소 수량<input type="number" min={1} max={cancelTarget?.remainingQuantity} value={cancelQuantity} disabled={cancelling}
        onChange={event => setCancelQuantity(Number(event.target.value))} /></CancelQuantity>
    </AdminCancellationDialog>
  </Section>;
}

export function LiveOrderHistory({ liveSeq, refreshVersion, canGift, giftDisabled }: { liveSeq: number; refreshVersion?: number; canGift?: boolean; giftDisabled?: boolean }) {
  return <PurchaseList key={liveSeq} liveSeq={liveSeq} refreshVersion={refreshVersion} canGift={canGift} giftDisabled={giftDisabled} full history />;
}

export default function LivePurchases({ liveSeq, refreshVersion, rehearsal = false, orderHistoryOnly = false, history = false, label, className, canGift, giftDisabled, inPagePopup = false }: { liveSeq: number; refreshVersion?: number; rehearsal?: boolean; orderHistoryOnly?: boolean; history?: boolean; label?: string; className?: string; canGift?: boolean; giftDisabled?: boolean; inPagePopup?: boolean }) {
  const [popup, setPopup] = useState<Window | null>(null), [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  useEffect(() => {
    if (!popup) return;
    const sync = () => { for (const name of ['data-admin-design', 'data-theme', 'class', 'style', 'lang']) {
      const value = document.documentElement.getAttribute(name);
      if (value === null) popup.document.documentElement.removeAttribute(name); else popup.document.documentElement.setAttribute(name, value);
    } };
    sync(); const observer = new MutationObserver(sync); observer.observe(document.documentElement, { attributes: true });
    const check = window.setInterval(() => { if (popup.closed) setPopup(null); }, 500);
    const close = () => { if (!popup.closed) popup.close(); };
    window.addEventListener('pagehide', close);
    return () => { observer.disconnect(); window.clearInterval(check); window.removeEventListener('pagehide', close); close(); };
  }, [popup, liveSeq]);
  useEffect(() => { setPopup(null); setDialogOpen(false); }, [liveSeq]);
  const open = () => {
    if (inPagePopup) { setDialogOpen(true); setError(''); return; }
    if (popup && !popup.closed) { popup.focus(); return; }
    const next = window.open('', '_blank', 'popup=yes,width=1280,height=900,resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=no');
    if (!next) { setError('구매 목록 창이 차단되었습니다. 팝업을 허용한 뒤 다시 눌러주세요.'); return; }
    try {
      next.document.title = history ? '주문내역 | 팬버드' : '전체 실시간 구매 목록 | 팬버드';
      const base = next.document.createElement('base'); base.href = document.baseURI; next.document.head.appendChild(base);
      const viewport = next.document.createElement('meta'); viewport.name = 'viewport'; viewport.content = 'width=device-width, initial-scale=1'; next.document.head.appendChild(viewport);
      document.querySelectorAll('link[rel="stylesheet"], style:not([data-styled])').forEach(style => next.document.head.appendChild(style.cloneNode(true)));
      next.document.body.style.margin = '0'; setPopup(next); setError(''); next.focus();
    } catch { next.close(); setError('구매 목록 창을 열지 못했습니다. 다시 시도해주세요.'); }
  };
  const sessionKey = `${liveSeq}:${rehearsal}`;
  return <>{history ? <AdminActionButton type="button" className={className} aria-label={label} onClick={event => { event.stopPropagation(); open(); }}>
    <LiveToolbarIcon name="list" />주문내역</AdminActionButton> : <PurchaseList key={sessionKey} liveSeq={liveSeq} refreshVersion={refreshVersion} orderHistoryOnly={orderHistoryOnly} onOpen={open} />}{error && <p role="alert">{error}</p>}
    {popup && !popup.closed && createPortal(<StyleSheetManager target={popup.document.head}><PopupContent onClick={event => event.stopPropagation()}>
      <PurchaseList key={sessionKey} liveSeq={liveSeq} full history={history} refreshVersion={refreshVersion} ownerDocument={popup.document} canGift={canGift} giftDisabled={giftDisabled} />
    </PopupContent></StyleSheetManager>, popup.document.body)}
    {inPagePopup && dialogOpen && createPortal(<div onClick={event => event.stopPropagation()}>
      <LiveDetailDialog open keepMounted title={label || '주문내역'} width={1280} height={800} onClose={() => setDialogOpen(false)}>
        <PopupContent $inline>
          <PurchaseList key={sessionKey} liveSeq={liveSeq} full history={history} refreshVersion={refreshVersion} canGift={canGift} giftDisabled={giftDisabled} />
        </PopupContent>
      </LiveDetailDialog>
    </div>, document.body)}
  </>;
}
const Section = styled.section`
  flex-shrink: 0;
  box-sizing: border-box; min-width: 0; padding: var(--space-4); border: 1px solid var(--color-border-default); border-radius: var(--radius-lg);
  background: var(--color-bg-surface); color: var(--color-text-primary);
  > p { color: var(--color-text-secondary); font-size: var(--text-body); }
  > p[role="alert"] { color: var(--color-text-danger); }
`;
const Header = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: var(--space-3); margin-bottom: var(--space-3);
  h2 { margin: 0; font-size: var(--text-body-lg); font-weight: 600; }
  p { margin: var(--space-1) 0 0; color: var(--color-text-secondary); font-size: var(--text-caption); }
`;
const EmptyState = styled.div`
  > svg { display: none; }
  > p { color: var(--color-text-secondary); font-size: var(--text-body); }
`;
const HistorySearch = styled.input`
  box-sizing: border-box; width: min(100%, calc(var(--control-sm) * 8)); min-height: var(--control-sm);
  padding: 0 var(--space-2); border: 1px solid var(--color-border-default); border-radius: var(--radius-md);
  background: var(--color-bg-surface); color: var(--color-text-primary); font: inherit;
  &::placeholder { color: var(--color-text-disabled); }
  &:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
  @media (pointer: coarse) { min-height: var(--touch-target); }
`;
const TableScroll = styled.div<{ $full: boolean }>`
  overflow: auto; max-height: ${props => props.$full ? 'calc(100dvh - 210px)' : '170px'}; overscroll-behavior: contain;
  thead { position: sticky; top: 0; z-index: 1; background: var(--color-bg-surface); }
`;
const Table = styled.table<{ $full: boolean }>`
  width: 100%; min-width: ${props => props.$full ? '720px' : '0'}; border-collapse: collapse; font-size: var(--text-body);
  th, td { text-align: left; padding: var(--space-2) var(--space-1); border-bottom: 1px solid var(--color-border-default); }
  th { color: var(--color-text-secondary); font-weight: 600; white-space: nowrap; }
  td:first-child, td:nth-child(5), td:nth-child(6) { white-space: nowrap; font-variant-numeric: tabular-nums; }
  small { display: block; color: var(--color-text-secondary); margin-top: var(--space-1); }
`;
const Pages = styled.div`
  display: grid; grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr); align-items: center;
  gap: var(--space-3); margin-top: var(--space-4);
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;
const PageNavigation = styled.div`
  grid-column: 2; display: flex; align-items: center; justify-content: center; gap: var(--space-3);
  @media (max-width: 640px) { grid-column: 1; }
`;
const PageSizeOptions = styled.div`
  grid-column: 3; justify-self: end;
  @media (max-width: 640px) { grid-column: 1; }
`;
const CancelQuantity = styled.label`display: flex; align-items: center; gap: var(--space-3); margin-bottom: var(--space-4);
  input { box-sizing: border-box; width: calc(var(--control-sm) * 3); min-height: var(--control-sm); padding: 0 var(--space-2); font: inherit;
    border: 1px solid var(--color-border-default); border-radius: var(--radius-md); background: var(--color-bg-surface); color: var(--color-text-primary); }
`;
const PopupContent = styled.main<{ $inline?: boolean }>`
  box-sizing: border-box; min-height: 0; height: ${p => p.$inline ? '100%' : '100dvh'};
  padding: ${p => p.$inline ? '0' : 'var(--space-5)'}; background: var(--color-bg-page); color: var(--color-text-primary);
  display: flex; flex-direction: column;
  > ${Section} { flex: 1; min-height: 0; display: flex; flex-direction: column; }
  ${Header} { flex-shrink: 0; }
  ${TableScroll} { flex: 1; min-height: 0; max-height: none; }
  ${EmptyState} {
    flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: var(--space-3); padding: var(--space-4); color: var(--color-text-secondary); text-align: center;
    > svg { display: block; width: var(--space-12); height: var(--space-12); flex-shrink: 0; }
    > p { margin: 0; }
  }
  ${Pages} { flex-shrink: 0; margin-top: auto; padding-top: var(--space-4); }
`;

const StatusActions = styled.div`
  display: flex; align-items: center; gap: var(--space-4);
  > span { min-width: 72px; white-space: nowrap; }
  > div { display: inline-flex; align-items: center; gap: var(--space-2); }
`;
